import React, { useState, useCallback } from 'react';
import { Search, Filter, Sparkles, TrendingUp, Users, Calendar, Star, AlertCircle, ChevronDown, ChevronUp, X, Loader2 } from 'lucide-react';
import { getCurrentBrand } from '../brand-config';
import { ragService, RAGSearchResult, RAGSearchMetadata } from '../services/ragService';
import ReviewDetailModal from './ReviewDetailModal';
import { Review, Highlight as ReviewHighlight } from '../types/Review';
import '../styles/RAGSearchPanel.css';

interface SearchFilters {
  sentiment: string[];
  rating: number[];
  source: string[];
  platform: string[];
  dateRange: string;
  hasTransformation: boolean;
  emotionalIntensity: string[];
  priority: string[];
  mediaType: string[];
  topics: string[];
  angles: string[];
  professions: string[];
}

interface RAGSearchPanelProps {
  onClose?: () => void;
}

const RAGSearchPanel: React.FC<RAGSearchPanelProps> = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const [filters, setFilters] = useState<SearchFilters>({
    sentiment: [],
    rating: [],
    source: [],
    platform: [],
    dateRange: 'all',
    hasTransformation: false,
    emotionalIntensity: [],
    priority: [],
    mediaType: [],
    topics: [],
    angles: [],
    professions: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchResults, setSearchResults] = useState<RAGSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [metadata, setMetadata] = useState<RAGSearchMetadata | null>(null);
  const [showPreviewBanner, setShowPreviewBanner] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [onlyWithInsights, setOnlyWithInsights] = useState(false);
  
  // Get current brand
  const currentBrand = getCurrentBrand();

  // Sample queries for demonstration - brand specific
  const sampleQueries = currentBrand === 'marsmen' ? [
    "What do customers say about energy improvements?",
    "Find reviews mentioning testosterone benefits",
    "Show me transformation stories from verified buyers",
    "What are the most common side effects mentioned?",
    "How long before customers see results?",
    "Which age group sees the best results?",
    "What do users say about libido improvements?"
  ] : [
    "What do customers say about warmth in extreme cold?",
    "How do these socks perform during hunting trips?",
    "Find reviews about moisture-wicking capabilities",
    "What professions recommend these socks?",
    "How long do the socks typically last?",
    "Do they really prevent blisters on long hikes?",
    "What do customers say about odor resistance after multiple days?"
  ];

  // Mock search function for preview
  const buildFilterPayload = useCallback(() => {
    const payload: Record<string, unknown> = {};

    if (filters.sentiment.length) payload.sentiment = filters.sentiment;
    if (filters.rating.length) payload.rating = filters.rating;
    if (filters.source.length) payload.source = filters.source;
    if (filters.platform.length) payload.platform = filters.platform;
    if (filters.priority.length) payload.priority = filters.priority;
    if (filters.mediaType.length) payload.media_type = filters.mediaType;
    if (filters.emotionalIntensity.length) payload.emotional_intensity = filters.emotionalIntensity;
    if (filters.hasTransformation) payload.has_transformation = true;
    if (filters.topics.length) payload.topics = filters.topics;
    if (filters.angles.length) payload.angles = filters.angles;
    if (filters.professions.length) payload.professions = filters.professions;

    if (filters.dateRange && filters.dateRange !== 'all') {
      payload.date_range = filters.dateRange === '1y' ? '365d' : filters.dateRange;
    }

    if (onlyWithInsights) payload.only_with_insights = true;

    return payload;
  }, [filters, onlyWithInsights]);

  const runSearch = useCallback(async (overrideQuery?: string) => {
    const normalizedQuery = (overrideQuery ?? query).trim();
    if (!normalizedQuery) {
      return;
    }

    setQuery(normalizedQuery);
    setIsSearching(true);
    setErrorMessage(null);
    setHasSearched(true);
    setSelectedReview(null);

    try {
      const filterPayload = buildFilterPayload();
      const response = await ragService.search({
        query: normalizedQuery,
        filters: Object.keys(filterPayload).length ? (filterPayload as any) : undefined,
        top_k: 20,
        include_raw_results: true,
      });

      setAiResponse(response.response);
      setSearchResults(response.results ?? []);
      setMetadata(response.metadata ?? null);
      if (showPreviewBanner) {
        setShowPreviewBanner(false);
      }
    } catch (err) {
      console.error('Failed to execute RAG query', err);
      setErrorMessage('Failed to run AI search. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [query, buildFilterPayload, showPreviewBanner]);

  const handleFilterToggle = (category: keyof SearchFilters, value: any) => {
    setFilters(prev => {
      const updated = { ...prev };
      
      if (Array.isArray(updated[category])) {
        const arr = updated[category] as any[];
        const index = arr.indexOf(value);
        if (index > -1) {
          arr.splice(index, 1);
        } else {
          arr.push(value);
        }
      } else {
        (updated[category] as any) = value;
      }
      
      return updated;
    });
  };

  const activeFilterCount = Object.values(filters).reduce((count, value) => {
    if (Array.isArray(value)) return count + value.length;
    if (typeof value === 'boolean' && value) return count + 1;
    if (typeof value === 'string' && value !== 'all') return count + 1;
    return count;
  }, 0);

  const summary = metadata?.summary;
  const sentimentCounts = summary?.sentiments ?? {};
  const positiveCount = sentimentCounts['positive'] ?? 0;
  const negativeCount = sentimentCounts['negative'] ?? 0;
  const neutralCount = sentimentCounts['neutral'] ?? 0;
  const averageRating = summary?.average_rating ?? null;
  const totalResults = metadata?.results_count ?? searchResults.length;
  const appliedDateRange = metadata?.filters_applied?.date_range as string | undefined;

  const formatDate = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString();
  };

  const convertToReview = useCallback((result: RAGSearchResult): Review => {
    const highlights: ReviewHighlight[] = Array.isArray(result.highlights)
      ? result.highlights.map((highlight) => ({
          text: highlight.text,
          angle: highlight.angle || '',
          category: highlight.category,
          quality: typeof highlight.quality === 'number' ? highlight.quality : 0,
        }))
      : [];

    const ratingValue = result.rating ?? 0;
    const normalizedRating = Math.max(0, Math.min(5, Math.round(ratingValue)));

    return {
      id: result.id || `rag-${Date.now()}`,
      timestamp: result.createdAt || new Date().toISOString(),
      source: result.source || 'RAG',
      rating: normalizedRating,
      review: result.reviewBody,
      reviewer_name: result.reviewerName || undefined,
      reviewBody: result.reviewBody,
      reviewerName: result.reviewerName,
      createdAt: result.createdAt,
      sentiment: (result.sentiment as Review['sentiment']) || 'neutral',
      highlights,
      metadata: result.metadata ?? undefined,
      productTitle: result.metadata && typeof result.metadata.productTitle === 'string'
        ? (result.metadata.productTitle as string)
        : undefined,
      brand: metadata?.brand || currentBrand,
    } as Review;
  }, [metadata?.brand, currentBrand]);

  return (
    <div className="rag-search-panel">
      {showPreviewBanner && (
        <div className="preview-banner">
          <div className="preview-content">
            <Sparkles className="preview-icon" />
            <span>Preview Mode: AI-powered search coming soon with S3 Vectors integration</span>
          </div>
          <button onClick={() => setShowPreviewBanner(false)} className="preview-close">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="search-header" style={{ cursor: 'pointer' }} onClick={() => setIsExpanded(!isExpanded)}>
        <div style={{ flex: 1 }}>
          <h2><Sparkles size={20} /> AI Review Search</h2>
          <p>Ask questions about your reviews and get AI-powered insights</p>
        </div>
        <button 
          className="expand-toggle"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#667eea',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {isExpanded && (
      <div className="search-container">
        <div className="search-input-wrapper">
          <Search className="search-icon" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                runSearch();
              }
            }}
            placeholder="Ask anything about your reviews..."
            className="search-input"
            style={{ paddingLeft: '64px' }}
          />
          <button 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            {activeFilterCount > 0 && (
              <span className="filter-badge">{activeFilterCount}</span>
            )}
          </button>
          <button
            className={`insights-toggle ${onlyWithInsights ? 'active' : ''}`}
            onClick={() => setOnlyWithInsights((prev) => !prev)}
            title="Only include reviews with AI extractions"
          >
            Insights Only
          </button>
          <button 
            className="search-button"
            onClick={() => runSearch()}
            disabled={isSearching || !query.trim()}
          >
            {isSearching ? (
              <span className="search-loading">
                <Loader2 className="loading-spinner" size={16} />
                Searching...
              </span>
            ) : (
              'Search'
            )}
          </button>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-section">
              <h4>Sentiment</h4>
              <div className="filter-chips">
                {['positive', 'negative', 'neutral'].map(sentiment => (
                  <button
                    key={sentiment}
                    className={`filter-chip ${filters.sentiment.includes(sentiment) ? 'active' : ''}`}
                    onClick={() => handleFilterToggle('sentiment', sentiment)}
                  >
                    {sentiment}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-section">
              <h4>Rating</h4>
              <div className="filter-chips">
                {[5, 4, 3, 2, 1].map(rating => (
                  <button
                    key={rating}
                    className={`filter-chip ${filters.rating.includes(rating) ? 'active' : ''}`}
                    onClick={() => handleFilterToggle('rating', rating)}
                  >
                    <Star size={14} fill={filters.rating.includes(rating) ? 'currentColor' : 'none'} />
                    {rating}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-section">
              <h4>Source</h4>
              <div className="filter-chips">
                {['Meta', 'Junip'].map(source => (
                  <button
                    key={source}
                    className={`filter-chip ${filters.source.includes(source) ? 'active' : ''}`}
                    onClick={() => handleFilterToggle('source', source)}
                  >
                    {source}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-section">
              <h4>Date Range</h4>
              <select 
                value={filters.dateRange}
                onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                className="filter-select"
              >
                <option value="all">All time</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
            </div>

            <div className="filter-section">
              <h4>Content Type</h4>
              <div className="filter-chips">
                {['VIDEO', 'IMAGE', 'TEXT'].map(type => (
                  <button
                    key={type}
                    className={`filter-chip ${filters.mediaType.includes(type) ? 'active' : ''}`}
                    onClick={() => handleFilterToggle('mediaType', type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-section">
              <h4>Special Filters</h4>
              <div className="filter-chips">
                <button
                  className={`filter-chip ${filters.hasTransformation ? 'active' : ''}`}
                  onClick={() => setFilters(prev => ({ ...prev, hasTransformation: !prev.hasTransformation }))}
                >
                  <TrendingUp size={14} />
                  Transformation Stories
                </button>
                {['GOLD', 'SILVER'].map(priority => (
                  <button
                    key={priority}
                    className={`filter-chip ${filters.priority.includes(priority) ? 'active' : ''}`}
                    onClick={() => handleFilterToggle('priority', priority)}
                  >
                    {priority}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="rag-error">
            <AlertCircle size={18} />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Sample queries */}
        {!hasSearched && (
          <div className="sample-queries">
            <p>Try these example questions:</p>
            <div className="query-suggestions">
              {sampleQueries.slice(0, 5).map((sq, idx) => (
                <button
                  key={idx}
                  className="query-suggestion"
                  onClick={() => runSearch(sq)}
                >
                  {sq}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* AI Response */}
        {aiResponse && (
          <div className="ai-response">
            <div className="ai-response-header">
              <Sparkles className="ai-icon" />
              <h3>AI Analysis</h3>
            </div>
            <div className="ai-response-content">
              {aiResponse.split('\n').map((line, idx) => {
                // Parse markdown-style formatting
                const formattedLine = line
                  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') // Bold
                  .replace(/\*([^*]+)\*/g, '<em>$1</em>'); // Italic
                
                return (
                  <p key={idx} dangerouslySetInnerHTML={{ __html: formattedLine }} />
                );
              })}
            </div>
          </div>
        )}

        {hasSearched && !isSearching && (totalResults > 0 || averageRating !== null) && (
          <div className="rag-summary-cards">
            <div className="summary-card">
              <Sparkles size={18} />
              <div>
                <span className="summary-label">Matches</span>
                <span className="summary-value">{totalResults}</span>
              </div>
            </div>
            {averageRating !== null && (
              <div className="summary-card">
                <Star size={18} />
                <div>
                  <span className="summary-label">Avg. Rating</span>
                  <span className="summary-value">{averageRating.toFixed(2)}</span>
                </div>
              </div>
            )}
            {(positiveCount || negativeCount || neutralCount) && (
              <div className="summary-card">
                <Users size={18} />
                <div>
                  <span className="summary-label">Sentiment</span>
                  <span className="summary-value">
                    {positiveCount}↑ / {negativeCount}↓ / {neutralCount}≈
                  </span>
                </div>
              </div>
            )}
            {appliedDateRange && (
              <div className="summary-card">
                <Calendar size={18} />
                <div>
                  <span className="summary-label">Date Range</span>
                  <span className="summary-value">{appliedDateRange}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {hasSearched && !isSearching && searchResults.length === 0 && (
          <div className="no-results">
            <Sparkles size={18} />
            <span>No reviews matched your filters. Try broadening the query or adjusting filters.</span>
          </div>
        )}

        {searchResults.length > 0 && (
          <div className="search-results">
            <h3>Relevant Reviews ({totalResults})</h3>
            <div className="results-list">
              {searchResults.map(result => (
                <div
                  key={result.id}
                  className="result-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedReview(convertToReview(result))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedReview(convertToReview(result));
                    }
                  }}
                >
                  <div className="result-header">
                    <div className="result-meta">
                      <span className="reviewer-name">{result.reviewerName || 'Anonymous'}</span>
                      {result.createdAt && (
                        <span className="result-date">{formatDate(result.createdAt)}</span>
                      )}
                      <span className={`result-sentiment ${result.sentiment || 'neutral'}`}>
                        {(result.sentiment || 'unknown').toUpperCase()}
                      </span>
                    </div>
                    <div className="result-score">
                      <span className="relevance">
                        {Math.round((result.score ?? 0) * 100)}% match
                      </span>
                      <div className="rating">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={14}
                            fill={i < Math.round(result.rating ?? 0) ? '#FFD700' : 'none'}
                            color={i < Math.round(result.rating ?? 0) ? '#FFD700' : '#ddd'}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="result-body">
                    <p>{result.reviewBody}</p>
                  </div>

                  {result.highlights && result.highlights.length > 0 && (
                    <div className="result-highlights">
                      <strong>Highlights:</strong>
                      <div className="highlight-list">
                        {result.highlights.slice(0, 3).map((highlight, idx) => (
                          <span key={idx} className="highlight-chip">
                            {highlight.text}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="result-footer">
                    <span>{result.source}</span>
                    {result.platform && <span>• {result.platform}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      )}
      {selectedReview && (
        <ReviewDetailModal
          review={selectedReview}
          onClose={() => setSelectedReview(null)}
        />
      )}
    </div>
  );
};

export default RAGSearchPanel;
